import { Component, Input, OnInit } from '@angular/core';
import * as moment from 'moment';
import { EventType, Roster } from '../../../../../../models/event-type';
import { CustomerSearchResult, isGuardianWithEmail } from '../../../../../../models/customer';
import { WaitlistEntry, WaitlistEntryStatus } from '../../../../../../models/scheduled-event';
import { Subject } from 'rxjs';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getAge,
  getISO8601DateStringFromMoment,
  propExists,
} from '../../../../../../utils';
import {
  blankInviteExpiry,
  InviteExpiry,
  inviteExpiriesEqual,
  inviteExpiryFromDeadline,
  inviteExpiryRequestFields,
  inviteExpiryValidationError
} from '../../../../../../models/invite-expiry';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CustomerService } from '../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import { debounceTime } from 'rxjs/operators';


@Component({
  selector: 'app-invite-to-roster-modal',
  templateUrl: './invite-to-roster-modal.component.html',
  styleUrls: ['./invite-to-roster-modal.component.css']
})
export class InviteToRosterModalComponent implements OnInit {
  @Input() eventTypeId: number;
  @Input() eventTypeName: string;
  @Input() preselectedRosterId: number = null;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public rosters: Roster[];
  public eventType: EventType;
  public hasFixedRoster: boolean;

  public selectedCustomer: CustomerSearchResult = null;
  public selectedRoster: Roster = null;
  public startDate = '';
  public inviteNote = '';
  public expiry: InviteExpiry = blankInviteExpiry();
  public sendInviteToParentsAndGuardians = false;

  public customerBelowMinAge = false;
  public customerHasLinkedAccountsOfSufficientAge: boolean | null = null;

  // Whether any linked parent or guardian has an email address. Null while
  // unknown -- either not looked up (the participant has an email of their own,
  // so it does not matter) or the lookup failed.
  public guardianWithEmailExists: boolean | null = null;

  // The participant's existing pending entry on the selected roster; its
  // start date and booking deadline are prefilled so that submitting
  // unedited re-sends rather than wipes them (PRO-1489).
  public existingPendingEntry: WaitlistEntry | null = null;

  // The invite endpoint overwrites the start date and deadline with whatever
  // is submitted, so a failed lookup must be shown, not let staff submit
  // blanks believing they are safe.
  public prefillLookupFailed = false;

  public loadingSubmit = false;
  public loadingRosters = true;
  public loadingEventType = true;
  public loadingCustomers = false;
  public loadingRelations = false;
  public loadingExistingEntry = false;

  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: CustomerSearchResult[] | null;

  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  // Used the same way: only the most recent lookup may prefill.
  private existingEntryRequestCount = 0;

  // What was prefilled, so the prefill can be undone on a selection change
  // without discarding anything staff typed themselves.
  private prefilledStartDate: string | null = null;
  private prefilledExpiry: InviteExpiry | null = null;

  // Used the same way for the relations lookups.
  private relationsRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService
  ) {}

  ngOnInit() {
    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });

    this.hasFixedRoster = (this.preselectedRosterId !== null);

    this.loadRosters();
    this.loadEventType();
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      (this.customerList.length > 0)
    );
  }

  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
    this.checkParticipantAge();
    this.loadExistingPendingEntry();
  }

  /**
   * Handle a change of roster. The prefill depends on both the participant and
   * the roster, so it has to be redone whenever either changes.
   */
  public handleRosterChange() {
    this.loadExistingPendingEntry();
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;
    this.selectedCustomer = null;
    // The prefill belongs to the deselected participant and must not be left
    // behind for whoever is picked next.
    this.clearPrefill();

    const orgConfig = this.orgService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public isLoading() {
    return (
      this.loadingSubmit ||
      this.loadingCustomers ||
      this.loadingRosters ||
      this.loadingEventType ||
      this.loadingRelations ||
      this.loadingExistingEntry
    );
  }

  /**
   * Whether this invitation has no address to go to (mirrors the recipients
   * `invite_customer_to_join_roster` assembles). With none, the email helper
   * returns without sending, so the invite succeeds silently; staff are
   * warned rather than blocked.
   */
  public hasNoEmailRecipient(): boolean {
    if (!this.selectedCustomer) { return false; }

    if (this.selectedCustomer.email) { return false; }

    if (this.existingPendingEntry &&
        (this.existingPendingEntry.notification_email ||
         this.existingPendingEntry.booking_customer_email)) {
      return false;
    }

    // Only claim nothing will be sent when we know no guardian can receive it.
    // A failed or pending lookup leaves this null, and an unproven warning is
    // worse than none.
    if (this.sendInviteToParentsAndGuardians &&
        (this.guardianWithEmailExists !== false)) {
      return false;
    }

    return true;
  }

  public submitButtonAvailable() {
    // handleSubmit returns silently while anything is in flight, including
    // the existing-entry lookup that fills in the start date and deadline.
    if (this.isLoading()) { return false; }

    if (!this.selectedRoster) { return false; }

    if (!this.selectedCustomer) { return false; }

    if (this.customerBelowMinAge && this.customerHasLinkedAccountsOfSufficientAge === false) {
      return false;
    }

    if (inviteExpiryValidationError(this.expiry, this.expiryTimeZone()) !== null) { return false; }

    return true;
  }

  public expiryTimeZone(): string | null {
    return this.selectedRoster ? this.selectedRoster.time_zone : null;
  }

  public handleSubmit() {
    if (this.isLoading()) { return; }
    if (!this.selectedRoster) { return; }
    if (!this.selectedCustomer) { return; }

    this.loadingSubmit = true;
    this.errorMessage = '';

    const expiryFields = inviteExpiryRequestFields(this.expiry);

    this.scheduledEventService
      .inviteCustomerToJoinRoster(
        this.selectedRoster.id,
        this.selectedCustomer.id,
        this.startDate,
        expiryFields.hoursToAccept,
        this.inviteNote,
        this.sendInviteToParentsAndGuardians,
        expiryFields.newDeadlineDate,
        expiryFields.newDeadlineTime
      )
      .then(() => {
        this.savedCallback({
          text: 'Invite sent.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to take this action.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  private loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response.results;
        }
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  private loadRosters() {
    this.loadingRosters = true;

    this.eventTypeService
      .getRosters(
        1,
        3000,
        {event_type_id: this.eventTypeId}
      )
      .then(result => {
        this.rosters = result.results;
        if (this.rosters.length === 1) {
          this.selectedRoster = this.rosters[0];
        }
        if (this.preselectedRosterId) {
          for (const roster of this.rosters) {
            if (roster.id === this.preselectedRosterId) {
              this.selectedRoster = roster;
            }
          }
        }

        // (ngModelChange) does not fire for the programmatic assignments
        // above, so this is what runs the prefill for a participant chosen
        // while this request was running.
        this.loadExistingPendingEntry();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingRosters = false; });
  }

  private loadEventType() {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(this.eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingEventType = false; });
  }

  /**
   * Prefill the start date and booking deadline from the participant's
   * pending entry on the selected roster, if any. Both fields overwrite what
   * the entry holds, so without this a re-invite silently discards them
   * (PRO-1489).
   */
  private loadExistingPendingEntry() {
    // A participant with no existing invitation must not inherit the last
    // one's prefill.
    this.clearPrefill();
    this.prefillLookupFailed = false;

    if (!this.selectedCustomer || !this.selectedRoster) { return; }

    this.loadingExistingEntry = true;

    // Increment the request count and save a local copy.
    this.existingEntryRequestCount++;
    const thisRequestCount = this.existingEntryRequestCount;

    this.eventTypeService
      .getEventTypeWaitlistEntries(
        this.eventTypeId,
        1,
        1,
        undefined,
        undefined,
        undefined,
        this.selectedCustomer.id,
        this.selectedRoster.id
      )
      .then(response => {
        // Only prefill if there has not been a more recent request initiated.
        if (thisRequestCount !== this.existingEntryRequestCount) { return; }

        const entry = response.results.find(
          e => (e.status === WaitlistEntryStatus.pending)
        );
        if (!entry) { return; }

        this.existingPendingEntry = entry;
        this.startDate = entry.start_date || '';
        this.expiry = inviteExpiryFromDeadline(entry.booking_deadline, this.selectedRoster.time_zone);
        this.prefilledStartDate = this.startDate;
        this.prefilledExpiry = { ...this.expiry };
      })
      .catch(() => {
        // This lookup needs can_manage_members, which sending an invite does
        // not, so a failure warns rather than takes the modal down.
        if (thisRequestCount !== this.existingEntryRequestCount) { return; }
        this.prefillLookupFailed = true;
      })
      .finally(() => {
        if (thisRequestCount === this.existingEntryRequestCount) {
          this.loadingExistingEntry = false;
        }
      });
  }

  /**
   * Undo the prefill, leaving alone any value staff have since edited: only a
   * field still holding exactly what was prefilled into it is reverted.
   */
  private clearPrefill() {
    this.existingPendingEntry = null;

    if ((this.prefilledStartDate !== null) &&
        (this.startDate === this.prefilledStartDate)) {
      this.startDate = '';
    }
    if ((this.prefilledExpiry !== null) &&
        inviteExpiriesEqual(this.expiry, this.prefilledExpiry)) {
      this.expiry = blankInviteExpiry();
    }

    this.prefilledStartDate = null;
    this.prefilledExpiry = null;
  }

  private checkParticipantAge() {
    this.customerBelowMinAge = false;
    this.customerHasLinkedAccountsOfSufficientAge = null;
    this.guardianWithEmailExists = null;

    const orgConfig = this.orgService.getOrganizationConfig();
    const minAge = orgConfig.membership_minimum_age;

    if (minAge && this.selectedCustomer?.birthday &&
        (getAge(this.selectedCustomer.birthday) < minAge)) {
      this.customerBelowMinAge = true;
      this.sendInviteToParentsAndGuardians = true;
    }

    // One fetch answers both questions: who is old enough to accept on the
    // participant's behalf, and whether a guardian could receive the email.
    if (this.customerBelowMinAge || !this.selectedCustomer?.email) {
      this.loadRelations(minAge);
    }
  }

  private loadRelations(minAge: number | null) {
    this.loadingRelations = true;
    this.customerHasLinkedAccountsOfSufficientAge = null;
    this.guardianWithEmailExists = null;

    // Increment the request count and save a local copy.
    this.relationsRequestCount++;
    const thisRequestCount = this.relationsRequestCount;

    this.customerService
      .getCustomerRelations(this.selectedCustomer.id)
      .then(result => {
        // Only apply if there has not been a more recent request initiated.
        if (thisRequestCount !== this.relationsRequestCount) { return; }

        if (this.customerBelowMinAge && minAge) {
          this.customerHasLinkedAccountsOfSufficientAge = result.results.some(r => {
            if (!r.related_customer_birthday) { return false; }
            return getAge(r.related_customer_birthday) >= minAge;
          });
        }

        this.guardianWithEmailExists = result.results.some(isGuardianWithEmail);
      })
      .catch(() => {
        if (thisRequestCount !== this.relationsRequestCount) { return; }
        this.customerHasLinkedAccountsOfSufficientAge = true; // fail open
        this.guardianWithEmailExists = null; // unknown
      })
      .finally(() => {
        if (thisRequestCount === this.relationsRequestCount) {
          this.loadingRelations = false;
        }
      });
  }
}
